// ISDP CUSTOM: shared profile hook. Reads the signed-in user's profile from the
// active auth provider (Okta OIDC in staging/prod; guest locally) via
// identityApi.getProfileInfo() — { displayName, email, picture }. Both the
// top-nav avatar (Root.tsx) and the Settings profile card consume this so they
// stay in sync and share the same graceful fallbacks.
import { useEffect, useState } from 'react';
import { identityApiRef, useApi, type ProfileInfo } from '@backstage/core-plugin-api';

export interface DevHubProfile {
  displayName?: string;
  email?: string;
  /** Provider-supplied avatar URL (e.g. Okta). Undefined for guest / no picture. */
  picture?: string;
  /** Up-to-2-letter initials derived from the display name / email, for the fallback avatar. */
  initials: string;
  loading: boolean;
}

// "Jane Doe" -> "JD"; "jane.doe@draeger.com" -> "JD"; "admins" -> "AD".
export function computeInitials(displayName?: string, email?: string): string {
  const raw = (displayName || email || '').trim();
  if (!raw) return '';
  const base = raw.includes('@') ? raw.slice(0, raw.indexOf('@')) : raw;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return base.slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function useProfileInfo(): DevHubProfile {
  const identityApi = useApi(identityApiRef);
  const [profile, setProfile] = useState<ProfileInfo>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    identityApi
      .getProfileInfo()
      .then(info => {
        if (active) {
          setProfile(info ?? {});
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [identityApi]);

  return {
    displayName: profile.displayName,
    email: profile.email,
    picture: profile.picture,
    initials: computeInitials(profile.displayName, profile.email),
    loading,
  };
}
